import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Api } from '../typings/api';
import store from '../store';
import { StoreState } from '../reducers/index';
import NativeApi from './nativeApi';
import UrlUtils from './url';
import UserAgentUtils from './userAgent';
import { AppType } from '../containers/App/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { DriveListState } from '../containers/DriveItemList/reducer';
import { AuthenticationUserState } from '../containers/Authentication/reducer';
import { DownloadStatus, OfflineManagerState, OfflineManagerStatus } from '../containers/OfflineManager/reducer';
import { RoutePath, RouteParams, RouteParamsDriveItemListPage, RouteParamsDriveItemDetailPage, RouteParamsTheater, RouteParamsStaticPage } from '../routes/constants';
import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { getOrFetchDriveItemMeta } from '../containers/DriveItemList/actions';
import DriveItemUtils from './driveItem';
import ApiUtils from './api';
import AuthUtils from './authentication';
import { MultiAppAuthenticationCredentials } from '../containers/MultiAppAuthentication/reducer';
import { ThumbType, LogoType } from '../containers/OfflineManager/reducer';
import { RouterParamsState } from '../containers/SetRouteParams/reducer';

const defaultBrandIcon = require('../icons/webpublication.svg');
const I18n = require('react-redux-i18n').I18n;


export interface HttpHeader {
	key: string;
	value: string;
}

export interface GalleryCredentials {
	login: string;
	password: string;
}

export enum ScreenFormat {
	Tablet = 'tablet',
	Mobile = 'mobile',
	Desktop = 'desktop'
}

export const downloadsFolder = 'downloads';
export const tempFolder = 'tmp';
export const thumbnailsFolder = 'thumbnails';
export const iconsFolder = 'icons';

export default class Utils {

	static deviceRootPath = window.location.origin + window.location.pathname.replace('indexConteneur.html', '') + '../..';

	static isGalleryReady = (galleryInfos: GalleryInfosState, offlineManager: OfflineManagerState, appType: AppType): boolean => {
		return galleryInfos.isConfigLoaded && offlineManager.status === OfflineManagerStatus.INITIALIZED
		&& (appType === AppType.Single ? galleryInfos.isLocalTemplateLoaded : galleryInfos.isRemoteTemplateLoaded);
	}

	static getGalleryName = (config: Api.Config, driveList: DriveListState, dispatch: Dispatch<StoreState>): string => {
		if (!driveList.driveItems[config.applicationGallery]) {
			dispatch(getOrFetchDriveItemMeta(config.applicationGallery));
			return '';
		}
		return I18n.t('page_title.gallery_name', {galleryName: driveList.driveItems[config.applicationGallery].label});
	}

	static getItemName = (driveList: DriveListState, id: number, dispatch: Dispatch<StoreState>): string => {
		if (!driveList.driveItems[id]) {
			dispatch(getOrFetchDriveItemMeta(id));
			return '';
		}
		return driveList.driveItems[id].label;
	}

	static getPageTitle = (route: RoutePath, params: RouteParams, config: Api.Config, driveList: DriveListState, dispatch: Dispatch<StoreState>): string => {
		const galleryName = Utils.getGalleryName(config, driveList, dispatch);
		let castedParams;
		let itemName;

		if (!galleryName) {
			return '';
		}

		// All RoutePath must be in the switch, do not add a default case
		switch (route) {
			case RoutePath.Default:
			case RoutePath.Home:
				return galleryName;
			case RoutePath.Authentication:
				return I18n.t('page_title.authentication');
			case RoutePath.ParametersPage:
				return I18n.t('page_title.parameters', { galleryName });
			case RoutePath.DriveItemListPage:
				castedParams = params as RouteParamsDriveItemListPage;
				return Utils.getDriveItemListPageTitle(castedParams, galleryName, driveList, dispatch);
			case RoutePath.Theater:
			case RoutePath.TheaterList:
				castedParams = params as RouteParamsTheater;
				itemName = driveList && castedParams.driveItemId ? Utils.getItemName(driveList, castedParams.driveItemId, dispatch) : '';
				return itemName ? I18n.t('page_title.theater', { galleryName, itemName }) : '';
			case RoutePath.DriveItemDetailPage:
				castedParams = params as RouteParamsDriveItemDetailPage;
				itemName = driveList && castedParams.driveItemId ? Utils.getItemName(driveList, castedParams.driveItemId, dispatch) : '';
				return itemName ? I18n.t('page_title.details', { galleryName, itemName }) : '';
			case RoutePath.NotFound:
				return I18n.t('messages.page_not_found');
			case RoutePath.StaticPage:
				castedParams = params as RouteParamsStaticPage;
				itemName = driveList && castedParams.component ? Utils.getItemName(driveList, parseInt(castedParams.component, 10), dispatch) : '';
				return `${itemName} - ${galleryName}`;
		}
	}

	static getDriveItemListPageTitle = (params: RouteParamsDriveItemListPage, galleryName: string, driveList, dispatch): string => {
		const origin: DriveItemListOrigin = params.origin;

		switch (origin) {
			case DriveItemListOrigin.Downloads:
				return I18n.t('page_title.downloads', { galleryName });
			case DriveItemListOrigin.Favorites:
				return I18n.t('page_title.favorites', {galleryName});
			case DriveItemListOrigin.Folder: {
				if (!params.originParam) {
					return I18n.t('page_title.drive', { galleryName });
				}
				const itemName = driveList && params.originParam ? Utils.getItemName(driveList, parseInt(params.originParam, 10), dispatch) : '';
				return itemName ? I18n.t('page_title.folder', { galleryName, itemName }) : '';
			}
			case DriveItemListOrigin.New:
				return I18n.t('page_title.new', { galleryName });
			case DriveItemListOrigin.Search:
				return I18n.t('page_title.search', { galleryName });
			case DriveItemListOrigin.Selection:
				return I18n.t('page_title.selection', { galleryName });
			case DriveItemListOrigin.Updates:
				return I18n.t('page_title.updates', { galleryName });
		}
	}

	static getBrandLogoName = (template: Api.Template, logoType: LogoType): string => {
		return template.brand && template.brand[logoType] ? template.brand[logoType] : '';
	}

	static getOriginBrandLogoUrl = (galleryInfos: GalleryInfosState, isHome: boolean, template?: Api.Template): string => {
		const resolvedTemplate = template || galleryInfos.template;
		const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;

		if (resolvedTemplate) {
			if (resolvedTemplate.brand && resolvedTemplate.brand[logoType]) {
				const brandLogo = resolvedTemplate.brand[logoType];
				const brandLogoFilename = Utils.getFilenameFromMediaId(brandLogo);
				return UrlUtils.getItemOriginalFileUrl(galleryInfos.config, brandLogoFilename);
			}
			return defaultBrandIcon;
		}
		return '';
	}

	static isMaster = (galleryConfig: Api.Config): boolean => {
		return galleryConfig.mediaPath !== undefined;
	}

	static getComponentUrl = (galleryInfos: GalleryInfosState, componentId: number, isAvailableOffline: boolean, isWelcomePage: boolean): string => {
		if (UserAgentUtils.isMobileApp && isAvailableOffline) {
			return `${Utils.getPersistentDownloadsFolder()}/${componentId}/index.html`;
		}

		return isWelcomePage ? UrlUtils.getWelcomePageUrl(galleryInfos) : UrlUtils.getComponentUrl(galleryInfos, componentId);
	}

	static getGalleryCredentials = (userInfos?: AuthenticationUserState): GalleryCredentials | null => {
		if (userInfos === undefined) {
			const storeState = store.getState() as StoreState;
			userInfos = storeState.authentication.userInfos;
		}
		const { mode } = userInfos;
		const isConnectedExcel = AuthUtils.isConnected(mode) && AuthUtils.isExcel(mode);

		if (userInfos.needCredentials && isConnectedExcel) {
			return {
				login: userInfos.login,
				password: userInfos.password,
			};
		} else {
			return null;
		}
	}

	static getMultiAppCredentials = (): MultiAppAuthenticationCredentials | undefined => {
		const storeState = store.getState() as StoreState;
		return storeState.multiAppAuthenticationInfos.credentials;
	}

	static getWelcomePageDownloadUrl = (galleryInfos: GalleryInfosState, template?: Api.Template): string => {
		const resolvedTemplate = template || galleryInfos.template;

		if (resolvedTemplate && resolvedTemplate.welcomepage) {
			const welcomePageUrl = resolvedTemplate.welcomepage.url;
			const welcomePageId = Utils.getMediaId(welcomePageUrl);
			return Utils.getComponentDownloadUrlById(galleryInfos, welcomePageId);
		}
		return '';
	}

	static getComponentDownloadUrl = (galleryInfos: GalleryInfosState, component: Api.DriveItem): string => {
		const componentId = component.id;
		return `${Utils.getComponentDownloadUrlById(galleryInfos, componentId)}`;
	}

	static getComponentDownloadUrlById = (galleryInfos: GalleryInfosState, componentId: number): string => {
		return `${UrlUtils.getResourceZipUrl(galleryInfos.config, componentId)}`;
	}

	static getMediaId(item: string): number {
		const regexMatches = /^\$\{media\}(.+)$/.exec(item);
		return regexMatches !== null ? parseInt(regexMatches[1], 10) : -1;
	}

	static getFilenameFromMediaId(mediaId: string): string {
		const filenameResult = /^\$media\$(.+)$/.exec(mediaId);
		return filenameResult ? filenameResult[1] : '';
	}

	static getBrandLogoUrl = (galleryInfos: GalleryInfosState, offlineManager: OfflineManagerState, isHome: boolean = false): string => {
		if (UserAgentUtils.isMobileApp) {
			const thumbType = isHome ? ThumbType.HOME_THUMB : ThumbType.BRAND_THUMB;
			const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;

			const { remoteConfig, localConfig } = offlineManager[thumbType];

			if (galleryInfos.isRemoteTemplateLoaded && remoteConfig.filename && remoteConfig.status === DownloadStatus.DOWNLOADED) {
				return `${Utils.getThumbnailsFolder()}/brand/${Utils.getFilenameFromMediaId(remoteConfig.filename)}`;
			} else if (!remoteConfig.filename && localConfig.filename && localConfig.status === DownloadStatus.DOWNLOADED) {
				return `${Utils.getThumbnailsFolder()}/brand/${Utils.getFilenameFromMediaId(localConfig.filename)}`;
			}

			if (galleryInfos.isRemoteTemplateLoaded && !Utils.getBrandLogoName(galleryInfos.template, logoType)) {
				return defaultBrandIcon; // if no logo has been set, we use the webpublication one
			}
			return ''; // if logo is downloading, nothing is rendered to avoid having several logos shown
		}
		return Utils.getOriginBrandLogoUrl(galleryInfos, isHome);
	}

	static getDeviceThumbnailPath = (galleryConfig: Api.Config, driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup }, isIcon: boolean) => {
		const thumbPath = isIcon ? DriveItemUtils.getIconOriginalPath(driveItem, galleryConfig) : DriveItemUtils.getThumbnail(driveItem, galleryConfig, resolutions, true);
		const extension = Utils.getFileExtension(thumbPath);
		return isIcon ? `${Utils.getIconsFolder()}/${driveItem.id}.${extension}` : `${Utils.getThumbnailsFolder()}/${driveItem.id}/card_thumbnail.${extension}`;
	}

	static getCustomIconUrl = (galleryConfig: Api.Config, driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup }): string => {
		if (UserAgentUtils.isMobileApp) {
			return `${Utils.getDeviceThumbnailPath(galleryConfig, driveItem, resolutions, true)}`;
		}
		return UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
	}

	static getPersistentDownloadsFolder = (): string => {
		return `${Utils.deviceRootPath}/${downloadsFolder}`;
	}

	static getRelativeDownloadsFolder = (): string => {
		return downloadsFolder;
	}

	static getTemporaryDownloadsFolder = (): string => {
		return `${Utils.deviceRootPath}/${tempFolder}`;
	}

	static getThumbnailsFolder = (): string => {
		return `${Utils.deviceRootPath}/${thumbnailsFolder}`;
	}

	static getIconsFolder = (): string => {
		return `${Utils.deviceRootPath}/${iconsFolder}`;
	}

	static isEmpty(object: Object): boolean {
		return !Object.keys(object).length;
	}

	static formatFileSize(size: number): string {
		const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
		let tmpSize = size;
		let i;

		for (i = 0; i < units.length; i++) {
			if (tmpSize < 1024) {
				return `${tmpSize.toFixed(1)} ${units[i]}`;
			}
			tmpSize = tmpSize / 1024;
		}
		return `${tmpSize.toFixed(1)} ${units[i]}`;
	}

	// Only works for pure Json object (functions & dates do not get copied properly)
	static deepCopy = <T>(source: T): T => {
		return JSON.parse(JSON.stringify(source));
	}

	static getSvgIcon(iconFileName: string, attributes: React.SVGProps<SVGUseElement> = {}): React.ReactNode {
		const useNode = React.createElement('use', { xlinkHref: `#svgicon-${iconFileName}`});
		return React.createElement('svg', attributes, useNode);
	}

	static nl2br(str: string): string {
		return str.replace(/(?:\r\n|\r|\n)/g, '<br />');
	}

	static promiseXHR(
		protocol: string,
		url: string,
		headers: HttpHeader[] = [],
		isAsync: boolean = true,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
		attempt: number = 0,
		ignoreConnectionStatus: boolean = false,
	): Promise<XMLHttpRequest> {
		return new Promise<XMLHttpRequest>((resolve, reject) => {
			const isOnline = UserAgentUtils.isOnline();
			if (ignoreConnectionStatus === false && isOnline !== true) {
				if (isOnline === false) {
					console.warn('xhr rejected: connection status is false', url);
					reject();
				}
				console.warn('xhr rejected: connection status not initialised', url);
				reject();
			}

			let urlWithParams = url;
			let payload = dataInBody;

			const includesSeparator = url.includes('?');
			const startingSeparator = includesSeparator ? '&' : '?';
			if (dataInUrl) {
				urlWithParams += startingSeparator + Utils.dataToUrl(dataInUrl);
			}
			const xhr = new XMLHttpRequest();
			xhr.open(protocol, urlWithParams, isAsync);

			headers.forEach(header => {
				if (header.key === 'overrideMimeType') {
					xhr.overrideMimeType(header.value);
				} else {
					if (header.key === 'Access-Control-Allow-Credentials' && header.value === 'true') {
						xhr.withCredentials = true;
					}
					xhr.setRequestHeader(header.key, header.value);
				}
			});

			xhr.onload = () => resolve(xhr);

			xhr.onabort = () => reject(xhr);

			// The hack resolving the promise if (xhr.readyState === 4 && xhr.status === 0) has been deleted, it may be useful for cross domain requests
			xhr.onerror = () => {
				if (attempt < 3 && url.includes(NativeApi.targetUrl)) {
					Utils.promiseXHR(protocol, url, headers, isAsync, dataInUrl, dataInBody, attempt + 1, ignoreConnectionStatus)
						.then(res => resolve(res))
						.catch(res => reject(res));
				} else {
					reject(xhr);
				}
			};

			xhr.send(payload);
		});
	}

	static dataToUrl(data: {}, mustEncode: boolean = true): string {
		let url = '';

		for (let i in data) {
			if (data[i] instanceof Array) {
				for (var j = 0; j < data[i].length; j++) {
					const key = mustEncode ? encodeURIComponent('[]') : '[]';
					const value = mustEncode ? encodeURIComponent(data[i][j]) : data[i][j];
					url += `&args${key}=${value}`;
				}
			} else {
				if (url) {
					url += '&';
				}
				url += `${i}=${mustEncode ? encodeURIComponent(data[i]) : data[i]}`;
			}

		}

		return url;
	}

	static fetchJson<T>(
		url: string,
		protocol: string = 'GET',
		isAsync: boolean = true,
		ignoreConnectionStatus: boolean = false,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
	): Promise<{} & T> {
		return Utils.fetchText(url, protocol, isAsync, ignoreConnectionStatus, dataInUrl, dataInBody).then(responseText => JSON.parse(responseText));
	}

	static fetchText(
		url: string,
		protocol: string = 'GET',
		isAsync: boolean = true,
		ignoreConnectionStatus: boolean = false,
		dataInUrl: {} | null = null,
		dataInBody: BodyInit | undefined = undefined,
	): | Promise<{} & string> {
		const headers = ApiUtils.getSecurityHeaderIfNeeded();
		return Utils.promiseXHR(protocol, url, headers, isAsync, dataInUrl, dataInBody, undefined, ignoreConnectionStatus)
			.then(response => {
				return response.status === 200 || response.status === 0
				? Promise.resolve(response.responseText)
				: Promise.reject(response);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	}

	static isResourceAvailable(url: string): Promise<XMLHttpRequest> {
		return new Promise<XMLHttpRequest>((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.timeout = 1000;
			xhr.open('GET', url, true);

			xhr.onload = () => {
				if (xhr.status === 200) {
					resolve(xhr);
				} else {
					reject(xhr);
				}
			};

			xhr.ontimeout = () => {
				if (xhr.status === 0) {
					resolve(xhr);
				} else {
					reject(xhr);
				}
			};

			xhr.send();
		});
	}

	static parseDate(dateString: string): Date {
		const dateStringSplit = dateString.split(' ');
		const dateSplit = dateStringSplit[0].split('-').map(e => parseInt(e, 10));
		const timeSplit = dateStringSplit[1].split(':').map(e => parseInt(e, 10));
		return new Date(dateSplit[0], dateSplit[1] - 1, dateSplit[2], timeSplit[0], timeSplit[1], timeSplit[2]);
	}

	static stringIsInStringEnum(value: string, actionKeys: {}) {
		for (let k in actionKeys) {
			if (actionKeys.hasOwnProperty(k) && actionKeys[k] === value) {
				return true;
			}
		}

		return false;
	}

	static getUrlWithCorrectProtocol(url: string) {
		const isMobileApp = UserAgentUtils.isMobileApp;
		const isHttpsNeeded = isMobileApp || window.location.protocol === 'https:';
		const newProtocol = isHttpsNeeded ? 'https' : 'http';

		const urlProtocolRegex = /^(.+):\/\//;
		const regexMatch = urlProtocolRegex.exec(url);

		if (regexMatch === null) {
			return `${newProtocol}://${url}`;
		}

		return url.replace(regexMatch[1], newProtocol );
	}

	static getFileExtension(fileName: string): string {
		if (fileName) {
			const extensionRegex = /(?:\.([^.]+))?(?:\?.*)?$/;
			const regexMatch = extensionRegex.exec(fileName);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}
		return '';
	}

	static getScreenFormat(): ScreenFormat {
		if (UserAgentUtils.isMobileApp && UserAgentUtils.isPhone()) {
			return ScreenFormat.Mobile;
		} else if (UserAgentUtils.isMobileApp && UserAgentUtils.isTablet()) {
			return ScreenFormat.Tablet;
		} else {
			const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

			if (screenWidth < 768) {
				return ScreenFormat.Mobile;
			} else if (screenWidth < 1200) {
				return ScreenFormat.Tablet;
			}
			return ScreenFormat.Desktop;
		}
	}

	public static generateId = (length: number = 15): string => {
		let text = '';
		let possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

		for (let i = 0; i < length; i++) {
			text += possibleChars.charAt(Math.floor(Math.random() * possibleChars.length));
		}
		return text;
	}

	static getCookie(cookieName: string): string {
		const cookieList = document.cookie.split(';');
		const key = `${cookieName}=`;
		const selectedCookie = cookieList.find(element => element.includes(cookieName));

		return selectedCookie ? selectedCookie.substring(key.length) : '';
	}

	static setCookie(cookieName: string, value: string, expireDays: number): void {
		const expireDate = new Date();
		expireDate.setTime(expireDate.getTime() + (expireDays * 24 * 60 * 60 * 1000));
		document.cookie = `${cookieName}=${value};expires=${expireDate.toUTCString()}`;
	}

	static isSecure = (appType: AppType, needCredentials: boolean): boolean => {
		return appType === AppType.Multiple || needCredentials;
	}

	static isGalleryRoot = (routerParamsHistory: RouterParamsState): boolean => {
		const { params, routes } = routerParamsHistory;
		const { origin, originParam } = params;
		const route = routes[routes.length - 1].path as RoutePath;

		return route === RoutePath.DriveItemListPage && origin === DriveItemListOrigin.Folder && !originParam;
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/index.ts